import React, { useState } from 'react';
import { Box, Text, Button, VStack } from '@chakra-ui/react';
import { useNavigate, useLocation } from 'react-router-dom';
import BackButton from '../components/BackButton';
import PhotoGallery from '../components/PhotoGallery';
import axios from 'axios';
import { useConfig } from '../contexts/ConfigContext';
import { useUser } from '../contexts/UserContext';
import { updateApplicationStatusInCache, sendLog, sendError, getCityLabel, getCountryLabel } from '../utils';
import { APPLICATION_STATUS, API_ROUTES, buildRoute, ROUTES } from '../constants';

const BusinessInfluencerDetailsPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { config } = useConfig();
  const { user } = useUser();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Helper function to format location string
  const formatLocation = (locationStr: string | undefined): string | undefined => {
    if (!locationStr) return undefined;
    if (locationStr.includes(',')) {
      const parts = locationStr.split(',').map(part => part.trim());
      if (parts.length === 2) {
        return `${getCityLabel(parts[0])}, ${getCountryLabel(parts[1])}`;
      }
    }
    return getCountryLabel(locationStr) || getCityLabel(locationStr) || locationStr;
  };

  const influencerData = location.state?.influencerData ? {
    ...location.state.influencerData,
    country: formatLocation(location.state.influencerData.country)
  } : null;

  const fromTab = location.state?.fromTab || APPLICATION_STATUS.PENDING;
  const eventId = location.state?.eventId || '';
  const eventName = location.state?.eventName || '';

  const handleBack = () => {
    console.log('Back button clicked, navigating to /business-influencers with tab:', fromTab);
    navigate(ROUTES.BUSINESS_INFLUENCERS, {
      state: {
        returnToTab: fromTab,
        eventId: eventId,
        eventName: eventName
      }
    });
  };

  const handleApprove = async () => {
    if (!config?.apiUrl || !influencerData?.applicationId || !eventId) {
      sendError(new Error('Missing required data for approval'), {
        context: 'BusinessInfluencerDetailsPage.handleApprove',
        hasApplicationId: !!influencerData?.applicationId,
        hasEventId: !!eventId,
        hasApiUrl: !!config?.apiUrl
      });
      return;
    }

    try {
      setLoading(true);
      setError(null);

      sendLog('BusinessApproveInfluencerAttempt', {
        applicationId: influencerData.applicationId,
        eventId,
        influencerId: influencerData.id
      });

      await axios.put(`${config.apiUrl}${buildRoute(API_ROUTES.APPLICATIONS_BY_ID, { id: influencerData.applicationId })}`, {
        status: APPLICATION_STATUS.APPROVED
      });

      updateApplicationStatusInCache(eventId, influencerData.applicationId, APPLICATION_STATUS.APPROVED);

      sendLog('BusinessApproveInfluencerSuccess', {
        applicationId: influencerData.applicationId,
        eventId,
        influencerId: influencerData.id
      });

      navigate(ROUTES.BUSINESS_INFLUENCERS, {
        state: {
          returnToTab: APPLICATION_STATUS.APPROVED,
          eventId: eventId,
          eventName: eventName
        }
      });
    } catch (err: any) {
      const errorToLog = err instanceof Error
        ? err
        : new Error(err.message || 'Failed to approve influencer');

      sendError(errorToLog, {
        context: 'BusinessInfluencerDetailsPage.handleApprove',
        applicationId: influencerData.applicationId,
        eventId,
        status: err.response?.status
      });

      setError(err.response?.data?.error || 'Failed to approve influencer');
      setLoading(false);
    }
  };

  const handleReject = async () => {
    if (!config?.apiUrl || !influencerData?.applicationId || !eventId) {
      sendError(new Error('Missing required data for rejection'), {
        context: 'BusinessInfluencerDetailsPage.handleReject',
        hasApplicationId: !!influencerData?.applicationId,
        hasEventId: !!eventId,
        hasApiUrl: !!config?.apiUrl
      });
      return;
    }

    try {
      setLoading(true);
      setError(null);

      sendLog('BusinessRejectInfluencerAttempt', {
        applicationId: influencerData.applicationId,
        eventId,
        influencerId: influencerData.id
      });

      await axios.put(`${config.apiUrl}${buildRoute(API_ROUTES.APPLICATIONS_BY_ID, { id: influencerData.applicationId })}`, {
        status: APPLICATION_STATUS.REJECTED
      });

      updateApplicationStatusInCache(eventId, influencerData.applicationId, APPLICATION_STATUS.REJECTED);

      sendLog('BusinessRejectInfluencerSuccess', {
        applicationId: influencerData.applicationId,
        eventId,
        influencerId: influencerData.id
      });

      navigate(ROUTES.BUSINESS_INFLUENCERS, {
        state: {
          returnToTab: fromTab,
          eventId: eventId,
          eventName: eventName
        }
      });
    } catch (err: any) {
      const errorToLog = err instanceof Error
        ? err
        : new Error(err.message || 'Failed to reject influencer');

      sendError(errorToLog, {
        context: 'BusinessInfluencerDetailsPage.handleReject',
        applicationId: influencerData.applicationId,
        eventId,
        status: err.response?.status
      });

      setError(err.response?.data?.error || 'Failed to reject influencer');
      setLoading(false);
    }
  };

  const photos = influencerData?.image ? [influencerData.image] : [];

  if (!influencerData) {
    return (
      <Box className="base-container" display="flex" alignItems="center" justifyContent="center" minH="100vh">
        <Text color="#F00C23">Influencer data not available</Text>
      </Box>
    );
  }

  return (
    <Box className="base-container">
      <PhotoGallery images={photos} />

      {/* Back Button */}
      <Box className="back-buttom-box">
        <BackButton
          onClick={handleBack}
          borderColor="#C6D959"
          backgroundColor="rgba(198, 217, 89, 0.1)"
        />
      </Box>

      {/* Influencer Name */}
      <Box
        position="absolute"
        top="320px"
        left="24px"
        zIndex={10}
      >
        <Text className="title-text-big">
          {influencerData.name}
        </Text>
      </Box>

      {/* Divider */}
      <Box
        className="navigation-bar-divider"
        top="370px"
      />

      {/* Completed Events Card */}
      <Box
        className='form-container'
        top="400px"
        display="flex"
        alignItems="center"
      >
        <Text
          className="title-text"
          marginRight="20px"
        >
          {influencerData.completedEvents}
        </Text>

        <Box
          width="1px"
          height="24px"
          background="linear-gradient(134deg, rgba(29, 27, 38, 1) 0%, rgba(107, 99, 140, 1) 50%, rgba(29, 27, 38, 1) 100%)"
          marginRight="20px"
        />

        <Text className="static-text-big-white">
          Completed events
        </Text>
      </Box>

      {/* Social Media Information */}
      <Box
        position="absolute"
        top="500px"
        left="24px"
        right="24px"
        zIndex={10}
      >
        <VStack spacing="10px" align="start">
          {influencerData.country && (
            <Text className="static-text-big-white">
              Location - {influencerData.country}
            </Text>
          )}

          {influencerData.instagram && (
            <Text className="static-text-big-white">
              Instagram - {influencerData.instagram}
            </Text>
          )}

          {influencerData.tiktok && (
            <Text className="static-text-big-white">
              Tiktok - {influencerData.tiktok}
            </Text>
          )}

          {influencerData.youtube && (
            <Text className="static-text-big-white">
              Youtube - {influencerData.youtube}
            </Text>
          )}

          <Text className="static-text-big-white">
            Allergies - {influencerData.allergies || 'None'}
          </Text>
        </VStack>
      </Box>

      {/* Bottom Action Buttons */}
      {fromTab === 'candidates' && (
        <Box
          className='form-container'
          backgroundColor="#020105 !important"
          bottom="0px"
          display="flex"
          gap="6px"
        >
          <Button
            className='button-big'
            backgroundColor="#F00C23 !important"
            color="#FFFFFF !important"
            onClick={handleReject}
            isLoading={loading}
            isDisabled={loading}
            _hover={{
              backgroundColor: "#d00a1e"
            }}
          >
            Reject
          </Button>

          <Button
            className='button-big'
            onClick={handleApprove}
            isLoading={loading}
            isDisabled={loading}
            _hover={{
              backgroundColor: "#b8cc4a"
            }}
          >
            Approve
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default BusinessInfluencerDetailsPage;
